import React, { createContext, useContext, useState, useCallback } from "react";

const AdminAuthContext = createContext(null);
const STORAGE_KEY = "mbt_admin_auth";


const API_BASE = (
  typeof process !== "undefined" && process.env?.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL
    : process.env?.NODE_ENV === "development"
      ? "http://localhost:5000"
      : window.location.origin
).replace(/\/+$/, "");


export function AdminAuthProvider({ children }) {
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return false;
      const parsed = JSON.parse(stored);
      // Valid only if it has a real JWT token
      return !!(parsed?.token && parsed.token.startsWith("eyJ"));
    } catch {
      return false;
    }
  });

  // ── Now async — calls the real backend endpoint ──────────────
  const adminLogin = useCallback(async (email, password) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/login`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ email: email.trim(), password }),
      });

      const data = await res.json();

      if (!res.ok)
        return { ok: false, error: data.error || "Login failed" };

      // Store the real JWT — Admin.jsx adminToken() will find it
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ token: data.token }));
      setIsAdminLoggedIn(true);
      return { ok: true };

    } catch (err) {
      return { ok: false, error: "Network error — is the server running?" };
    }
  }, []);

  const adminLogout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setIsAdminLoggedIn(false);
  }, []);

  return (
    <AdminAuthContext.Provider value={{ isAdminLoggedIn, adminLogin, adminLogout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  return ctx;
}